/**
 * Imperative controller for MapLibre transit stop sources, layers, and interactions.
 */

import type { FeatureCollection } from "geojson";
import type maplibregl from "maplibre-gl";

import { MAP_STOPS_MIN_PRELOAD_ZOOM, MAP_STOPS_PRELOAD } from "../../../config/global.configuration";
import {
    logAppMemoryReport,
    recordMapStopsMemoryStats,
} from "../../../tools/AppMemoryProfiler.tools";
import { estimateJsonUtf8Bytes } from "../../../tools/MemoryEstimate.tools";
import type { StopWheelchairBoarding } from "../../../types/dtos/api/stop.dto";
import { BboxPreloadStore } from "../bboxPreload";
import {
    ensureStopsPreloadBboxDebugLayers,
    ensureStopsPreloadShrinkDebugLayers,
    removeStopsPreloadBboxDebugLayers,
    removeStopsPreloadShrinkDebugLayers,
    resolveStopsPreloadDebugBboxes,
    STOPS_DEBUG_BBOX_SOURCE_ID,
    STOPS_DEBUG_SHRINK_SOURCE_ID,
    updateStopsPreloadBboxDebugLayer,
    updateStopsPreloadShrinkDebugLayer,
} from "../mapDebugLayers";
import {
    STOP_CLICK_LAYER_IDS,
    STOPS_CHILDREN_SOURCE_ID,
    STOPS_PARENTS_SOURCE_ID,
} from "../mapLayerIds";
import {
    applyStopLayerTheme,
    EMPTY_STOPS_FEATURE_COLLECTION,
    ensureStopLayers,
    ensureStopSources,
    loadStopMapIcons,
    parseGeoJsonIsParentProperty,
    stopsDtosToFeatureCollections,
} from "./stopMapLayers.helpers";
import type { StopClickPayload, StopMapLayersAttachOptions } from "./stopMapLayers.types";
import type { PreloadedStopGroups, StopViewportContext } from "./stopPreload.types";
import {
    defaultFetchStopsForBbox,
    handleStopsFetchError,
    mapBoundsToStopBbox,
} from "./stopPreloadBbox.tools";
import { findChildStopWheelchairBoardingByPlatform, parseGeoJsonWheelchairBoardingProperty } from "./stopWheelchairBoarding.tools";

/**
 * Manages stop GeoJSON sources, symbol layers, bbox preload fetching, and click handlers
 * on a MapLibre map instance.
 */
export class StopMapLayersController {

    private map: maplibregl.Map | null = null;

    private onStopClick: ( ( payload: StopClickPayload ) => void ) | null = null;

    private isAttached = false;

    private readonly preloadStore = new BboxPreloadStore<PreloadedStopGroups>(
        MAP_STOPS_MIN_PRELOAD_ZOOM,
        MAP_STOPS_PRELOAD,
        defaultFetchStopsForBbox,
        {
            "countItems":   ( data ) => data.parents.length + data.children.length,
            "loggerName":   "StopMapLayersController",
            "onFetchError": handleStopsFetchError,
        },
    );

    private debugShowBbox = false;

    private debugShowShrink = false;

    private viewportDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    /** Bumped on detach and at attach start to ignore stale async attach work after style swaps. */
    private attachGeneration = 0;

    private readonly handleStopClick = ( event: maplibregl.MapMouseEvent & { "features"?: maplibregl.MapGeoJSONFeature[] } ): void => {
        const feature = event.features?.[0];
        if ( feature === undefined || this.onStopClick === null ) {
            return;
        }

        const [ lng, lat ] = ( feature.geometry as GeoJSON.Point ).coordinates;
        const wheelchairBoarding = parseGeoJsonWheelchairBoardingProperty( feature.properties?.wheelchairBoarding );

        this.onStopClick( {
            "lngLat":       [ lng, lat ],
            "stopId":       String( feature.properties?.id ?? "" ),
            "stopName":     String( feature.properties?.name ?? "" ),
            "isParentStop": parseGeoJsonIsParentProperty( feature.properties?.isParent ),
            wheelchairBoarding,
        } );
    };

    private readonly handleStopMouseEnter = (): void => {
        this.map?.getContainer().classList.add( "map-pointer-cursor" );
    };

    private readonly handleStopMouseLeave = (): void => {
        this.map?.getContainer().classList.remove( "map-pointer-cursor" );
    };

    private readonly handleViewportSettled = (): void => {
        this.scheduleSyncStopsFromPreload();
    };

    /**
     * Attaches stop layers and interaction handlers to the map.
     * Idempotent: detaches existing handlers before re-attaching.
     *
     * @param map Target MapLibre map instance.
     * @param options Theme and click callback configuration.
     */
    async attach( map: maplibregl.Map, options: StopMapLayersAttachOptions ): Promise<void> {
        this.detach();

        const attachId = ++this.attachGeneration;

        this.map         = map;
        this.onStopClick = options.onStopClick;

        await loadStopMapIcons( map, options.isDarkUi );

        if ( this.isAttachCancelled( attachId, map ) ) {
            return;
        }

        ensureStopSources( map );
        ensureStopLayers( map, options.isDarkUi );

        if ( this.isAttachCancelled( attachId, map ) ) {
            return;
        }

        for ( const layerId of STOP_CLICK_LAYER_IDS ) {
            map.on( "click", layerId, this.handleStopClick );
            map.on( "mouseenter", layerId, this.handleStopMouseEnter );
            map.on( "mouseleave", layerId, this.handleStopMouseLeave );
        }

        map.on( "moveend", this.handleViewportSettled );
        map.on( "zoomend", this.handleViewportSettled );
        this.isAttached = true;

        await this.syncStopsFromPreload();
        this.syncPreloadDebugLayers();
    }

    /**
     * Enables or disables stop preload bbox / shrink debug overlays.
     *
     * @param options Per-layer visibility flags from the Layers dev modal.
     */
    setPreloadDebugVisibility( options: {
        "bbox":   boolean;
        "shrink": boolean;
    } ): void {
        this.debugShowBbox   = options.bbox;
        this.debugShowShrink = options.shrink;
        this.syncPreloadDebugLayers();
    }

    /**
     * Removes stop interaction handlers from the current map.
     * Sources and layers remain on the map until the style is replaced.
     */
    detach(): void {
        this.attachGeneration += 1;
        this.clearViewportDebounce();

        if ( this.map !== null && this.isAttached ) {
            for ( const layerId of STOP_CLICK_LAYER_IDS ) {
                this.map.off( "click", layerId, this.handleStopClick );
                this.map.off( "mouseenter", layerId, this.handleStopMouseEnter );
                this.map.off( "mouseleave", layerId, this.handleStopMouseLeave );
            }

            this.map.off( "moveend", this.handleViewportSettled );
            this.map.off( "zoomend", this.handleViewportSettled );
        }

        this.preloadStore.reset();
        this.debugShowBbox   = false;
        this.debugShowShrink = false;

        if ( this.map !== null ) {
            removeStopsPreloadBboxDebugLayers( this.map );
            removeStopsPreloadShrinkDebugLayers( this.map );
        }

        this.isAttached = false;
    }

    /**
     * Updates label colors and reloads pole icons when the UI theme changes.
     *
     * @param isDarkUi Whether dark UI palette is active.
     */
    async applyTheme( isDarkUi: boolean ): Promise<void> {
        if ( this.map === null ) {
            return;
        }

        await applyStopLayerTheme( this.map, isDarkUi );
    }

    /**
     * Resolves wheelchair boarding for a quai under a parent station using preloaded child stops.
     *
     * @param parentStopId Parent stop id from the popup.
     * @param platformCode Rider-facing platform code.
     * @returns Wheelchair boarding when a matching child stop is preloaded, otherwise `null`.
     */
    resolveWheelchairBoardingForPlatform(
        parentStopId: string,
        platformCode: string,
    ): StopWheelchairBoarding | null {
        const groups = this.preloadStore.getPreloadedData();

        return findChildStopWheelchairBoardingByPlatform(
            groups?.children ?? [],
            parentStopId,
            platformCode,
        );
    }

    /**
     * Whether an in-flight {@link attach} was superseded by detach or a newer attach.
     *
     * @remarks Does not use `map.isStyleLoaded()` — MapLibre can report `false` briefly after
     * `addSource` / `addLayer` even though the style is usable and layers were just added.
     * @param attachId Generation captured at attach start.
     * @param map Map instance passed to attach.
     * @returns True when this attach should abort.
     */
    private isAttachCancelled( attachId: number, map: maplibregl.Map ): boolean {
        return attachId !== this.attachGeneration || this.map !== map;
    }

    /**
     * Debounces preload sync after viewport move/zoom end.
     */
    private scheduleSyncStopsFromPreload(): void {
        this.clearViewportDebounce();

        this.viewportDebounceTimer = setTimeout( () => {
            this.viewportDebounceTimer = null;
            void this.syncStopsFromPreload();
        }, MAP_STOPS_PRELOAD.viewportDebounceMs );
    }

    /**
     * Clears the pending viewport debounce timer.
     */
    private clearViewportDebounce(): void {
        if ( this.viewportDebounceTimer !== null ) {
            clearTimeout( this.viewportDebounceTimer );
            this.viewportDebounceTimer = null;
        }
    }

    /**
     * Builds the viewport context snapshot for the preload store.
     *
     * @param map Active MapLibre map.
     * @returns Context or null when the container has no measurable size.
     */
    private buildViewportContext( map: maplibregl.Map ): StopViewportContext | null {
        const container = map.getContainer();
        const widthPx   = container.clientWidth;
        const heightPx  = container.clientHeight;

        if ( widthPx <= 0 || heightPx <= 0 ) {
            return null;
        }

        const center = map.getCenter();

        return {
            "visibleBbox":      mapBoundsToStopBbox( map.getBounds() ),
            "centerLat":        center.lat,
            "centerLng":        center.lng,
            "viewportWidthPx":  widthPx,
            "viewportHeightPx": heightPx,
            "currentZoom":      map.getZoom(),
        };
    }

    /**
     * Applies GeoJSON to parent/child stop sources.
     *
     * @param geojsonParents Parent station features.
     * @param geojsonChildren Platform stop features.
     */
    private applyStopsToMap(
        geojsonParents: FeatureCollection,
        geojsonChildren: FeatureCollection,
    ): void {
        const map = this.map;
        if ( map === null ) {
            return;
        }

        const parentsSource = map.getSource( STOPS_PARENTS_SOURCE_ID );
        const childrenSource = map.getSource( STOPS_CHILDREN_SOURCE_ID );

        if ( parentsSource !== undefined && "setData" in parentsSource ) {
            ( parentsSource as maplibregl.GeoJSONSource ).setData( geojsonParents );
        }

        if ( childrenSource !== undefined && "setData" in childrenSource ) {
            ( childrenSource as maplibregl.GeoJSONSource ).setData( geojsonChildren );
        }
    }

    /**
     * Clears stop sources to empty collections (below-min-zoom or first load failure without cache).
     */
    private clearStopsOnMap(): void {
        this.applyStopsToMap( EMPTY_STOPS_FEATURE_COLLECTION, EMPTY_STOPS_FEATURE_COLLECTION );
    }

    /**
     * Syncs map stop sources from the preload store (bbox-aware, sticky while loading).
     */
    async syncStopsFromPreload(): Promise<void> {
        const map = this.map;
        if ( map === null ) {
            return;
        }

        const context = this.buildViewportContext( map );
        if ( context === null ) {
            return;
        }

        const result = await this.preloadStore.updatePreloadIfNeeded( context );

        if ( result.status === "belowMinZoom" ) {
            this.clearStopsOnMap();

            return;
        }

        if ( result.status === "fetched" ) {
            const groups = this.preloadStore.getPreloadedData();
            if ( groups === null ) {
                return;
            }

            const { "parents": geojsonParents, "children": geojsonChildren } = stopsDtosToFeatureCollections( groups );
            this.applyStopsToMap( geojsonParents, geojsonChildren );

            recordMapStopsMemoryStats( {
                "parentFeatures": geojsonParents.features.length,
                "childFeatures":  geojsonChildren.features.length,
                "stopsFetched":   result.itemsFetched ?? 0,
                "estimatedBytes": estimateJsonUtf8Bytes( geojsonParents ) + estimateJsonUtf8Bytes( geojsonChildren ),
            } );

            logAppMemoryReport( { "context": "map-stops-loaded" } );

            if ( import.meta.env.DEV ) {
                console.debug(
                    "[StopMapLayersController]",
                    "syncStopsFromPreload",
                    result.status,
                    {
                        "stopsFetched": result.itemsFetched,
                        "fetchedBbox":  result.fetchedBbox,
                    },
                );
            }

            return;
        }

        if ( result.status === "failed" && this.preloadStore.getPreloadedData() === null ) {
            this.clearStopsOnMap();
        }

        this.syncPreloadDebugLayers();
    }

    /**
     * Updates stop preload bbox and shrink debug overlays when enabled.
     */
    private syncPreloadDebugLayers(): void {
        const map = this.map;
        if ( map === null ) {
            return;
        }

        if ( !this.debugShowBbox && !this.debugShowShrink ) {
            if ( map.getSource( STOPS_DEBUG_BBOX_SOURCE_ID ) !== undefined ) {
                updateStopsPreloadBboxDebugLayer( map, null );
            }

            if ( map.getSource( STOPS_DEBUG_SHRINK_SOURCE_ID ) !== undefined ) {
                updateStopsPreloadShrinkDebugLayer( map, null );
            }

            return;
        }

        const context = this.buildViewportContext( map );
        if ( context === null ) {
            return;
        }

        const { prefetchDisplayBbox, shrinkDisplayBbox } = resolveStopsPreloadDebugBboxes(
            context,
            this.preloadStore.getPreloadedBbox(),
        );

        if ( this.debugShowBbox ) {
            ensureStopsPreloadBboxDebugLayers( map );
            updateStopsPreloadBboxDebugLayer( map, prefetchDisplayBbox );
        } else if ( map.getSource( STOPS_DEBUG_BBOX_SOURCE_ID ) !== undefined ) {
            updateStopsPreloadBboxDebugLayer( map, null );
        }

        if ( this.debugShowShrink ) {
            ensureStopsPreloadShrinkDebugLayers( map );
            updateStopsPreloadShrinkDebugLayer( map, shrinkDisplayBbox );
        } else if ( map.getSource( STOPS_DEBUG_SHRINK_SOURCE_ID ) !== undefined ) {
            updateStopsPreloadShrinkDebugLayer( map, null );
        }
    }

}
